import { useEffect, useState } from "react";
import { Loader2, Eye, Link2, AlertTriangle, ExternalLink, CheckCircle2, Search } from "lucide-react";
import api from "@/lib/api";

const TABS = [
  { key: "services", label: "Hizmetler" },
  { key: "districts", label: "İlçe SEO" },
  { key: "district_hubs", label: "İlçe Hub" },
  { key: "blogs", label: "Bloglar" },
  { key: "applications", label: "Uygulamalar" },
];

function Flag({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold ${ok ? "bg-red-500/15 text-red-300" : "bg-green-500/10 text-green-400/80"}`}>
      {ok ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} {label}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5">
      <p className="font-oswald text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-neutral-500">{sub}</p>}
    </div>
  );
}

export default function SeoHubAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("services");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/seo-hub");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>;
  if (!data) return <div className="py-20 text-center text-neutral-500">SEO Hub verisi alınamadı.</div>;

  const rows = data.report[tab] || [];
  const sum = data.summary[tab] || {};

  return (
    <div data-testid="admin-seo-hub">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium text-white sm:text-3xl">SEO Hub</h1>
          <p className="mt-1 text-sm text-neutral-500">İçerik sağlığı, iç-link ve görüntülenme paneli</p>
        </div>
        <span className={`inline-flex items-center gap-2 border px-3 py-2 font-oswald text-[11px] uppercase tracking-[0.15em] ${data.search_console_connected ? "border-green-500/30 text-green-400" : "border-white/10 text-neutral-500"}`}>
          <Search className="h-3.5 w-3.5" /> Search Console: {data.search_console_connected ? "Bağlı" : "Bağlı değil (deploy sonrası)"}
        </span>
      </div>

      {/* Summary */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Toplam Görüntülenme" value={data.total_views} sub="Dahili takip (kendi sistemimiz)" />
        <StatCard label="Hizmet Sayfası" value={data.summary.services?.count || 0} sub={`${data.summary.services?.views || 0} görüntülenme`} />
        <StatCard label="İlçe Sayfası" value={data.summary.districts?.count || 0} sub={`${data.summary.district_hubs?.count || 0} ilçe hub`} />
        <StatCard label="Blog / Uygulama" value={`${data.summary.blogs?.count || 0} / ${data.summary.applications?.count || 0}`} />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`seo-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`border px-4 py-2 font-oswald text-xs uppercase tracking-[0.12em] transition-colors ${tab === t.key ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-white"}`}
          >
            {t.label} <span className="ml-1 text-neutral-600">{data.summary[t.key]?.count || 0}</span>
          </button>
        ))}
      </div>

      {/* Warnings summary for current tab */}
      <div className="mt-5 flex flex-wrap gap-4 text-sm text-neutral-400">
        <span>Eksik meta: <b className="text-red-300">{sum.missing_meta || 0}</b></span>
        <span>Eksik görsel: <b className="text-red-300">{sum.missing_image || 0}</b></span>
        <span>Eksik schema: <b className="text-red-300">{sum.missing_schema || 0}</b></span>
        <span>Eksik FAQ: <b className="text-red-300">{sum.missing_faq || 0}</b></span>
      </div>

      {/* Table */}
      <div className="mt-5 overflow-x-auto border border-white/10">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02] font-oswald text-[11px] uppercase tracking-[0.12em] text-neutral-500">
              <th className="px-4 py-3">Sayfa</th>
              <th className="px-4 py-3"><Eye className="inline h-4 w-4" /> Görüntülenme</th>
              <th className="px-4 py-3"><Link2 className="inline h-4 w-4" /> İç Link</th>
              <th className="px-4 py-3">Durum</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.slug} data-testid={`seo-row-${r.slug}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <span className="block font-medium text-white">{r.title || r.slug}</span>
                  <span className="text-xs text-neutral-600">{r.url}</span>
                </td>
                <td className="px-4 py-3 font-semibold text-gold">{r.views}</td>
                <td className="px-4 py-3 text-neutral-300">{r.internal_links}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {r.missing_meta && <Flag ok label="meta" />}
                    {r.missing_image && <Flag ok label="görsel" />}
                    {r.missing_schema && <Flag ok label="schema" />}
                    {r.missing_faq && <Flag ok label="FAQ" />}
                    {r.missing_fields?.length > 0 && <span className="inline-flex items-center gap-1 rounded bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-300"><AlertTriangle className="h-3 w-3" /> {r.missing_fields.join(", ")}</span>}
                    {!r.missing_meta && !r.missing_image && !r.missing_schema && !r.missing_faq && !(r.missing_fields?.length) && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-400/80"><CheckCircle2 className="h-3 w-3" /> Tam</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-neutral-400 hover:text-gold"><ExternalLink className="h-4 w-4" /></a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
